import {
    Award,
    BookOpen,
    CheckIcon,
    ChevronDown,
    Clock,
    LayoutGrid,
    Lock,
    Quote,
    Target,
    TrendingUp,
} from "lucide-react";
import {
    Accordion, AccordionDetails,
    AccordionGroup, AccordionSummary,
    Box,
    Button,
    Card,
    Chip,
    Grid,
    IconButton,
    Stack,
    Typography,
    useTheme,
} from "@mui/joy";
import {motion} from "motion/react";
import React, {useState} from "react";
import {Link, useNavigate} from "react-router-dom";
import GuestNavbar from "../../components/Layout/GuestNavbar";
import {subscriptions} from "../../values/mockSubscriptions";
import {DEFAULT_SUBSCRIPTION_PLANS, Subscription} from "../../models/subscription";
import PricingCardComponent from "../../components/Subscription/PricingCardComponent";
import {APP_URL} from "../../constants";
import {productionMode} from "../../utils/routes";
import {useTranslation} from "react-i18next";

const TRUST_BADGES = [
    {icon: Lock, label: "trustBadgeTitle1", sub: "trustBadgeSubtitle1"},
    {icon: LayoutGrid, label: "trustBadgeTitle2", sub: "trustBadgeSubtitle2"},
    {icon: Clock, label: "trustBadgeTitle3", sub: "trustBadgeSubtitle3"},
];

const WHY_ITEMS = [
    {
        title: "whyFeaturesTitle1",
        description: "whyFeaturesDescription1",
        icon: Award,
    },
    {
        title: "whyFeaturesTitle2",
        description: "whyFeaturesDescription2",
        icon: TrendingUp,
    },
    {
        title: "whyFeaturesTitle3",
        description: "whyFeaturesDescription3",
        icon: BookOpen,
    },
    {
        title: "whyFeaturesTitle4",
        description: "whyFeaturesDescription4",
        icon: Target,
    },
];

const EXAMS = [
    {
        name: "ieltsExamLabel",
        description: "ieltsExamShortInfo",
        color: ["#3b82f6", "#9333ea"],
    },
    {
        name: "toeflExamLabel",
        description: "toeflExamShortInfo",
        color: ["#ec4899", "#f97316"],
    },
    {
        name: "tcfExamLabel",
        description: "tcfExamShortInfo",
        color: ["#10b981", "#3b82f6"],
    },
    {
        name: "tcfCAExamLabel",
        description: "tcfCAExamShortInfo",
        color: ["#f59e0b", "#ef4444"],
    },
];

const TESTIMONIALS = [
    {
        quote: "testimonialQuote1",
        author: "testimonialAuthor1",
    },
    {
        quote: "testimonialQuote2",
        author: "testimonialAuthor2",
    },
    {
        quote: "testimonialQuote3",
        author: "testimonialAuthor3",
    },
]

const FAQ_ITEMS = [
    {
        q: "faqQuestion1",
        a: "faqAnswer1",
    },
    {
        q: "faqQuestion2",
        a: "faqAnswer2",
    },
    {
        q: "faqQuestion3",
        a: "faqAnswer3",
    },
]

const GuestHome = () => {
    const navigate = useNavigate()
    const [testimonialIndex, setTestimonialIndex] = useState(0)
    const {t} = useTranslation()

    const handleLogin = () => {
        if (productionMode)
            window.location.href = APP_URL + "/login"
        else navigate("/login")
    }
    const handleStartPreparing = (plan?: string) => {
        if (productionMode)
            window.location.href = APP_URL
        else navigate(`/register${plan ? `?plan=${plan}` : ""}`)
    }

    return (
        <Stack sx={{overflowX: "hidden", minHeight: "100vh"}}>
            <GuestNavbar/>

            {/* Hero */}
            <Stack
                paddingX={{xs: 2, sm: 4, lg: 8}}
                paddingY={{xs: 6, md: 8}}
                alignItems="center"
                textAlign="center"
                gap={3}>
                <motion.div
                    initial={{opacity: 0, y: 20}}
                    animate={{opacity: 1, y: 0}}
                    transition={{duration: 0.5}}>
                    <Typography
                        level="h1"
                        textAlign={"center"}
                        fontSize={"3.5rem"}
                        fontWeight={700}
                        sx={{
                            lineHeight: 1.2,
                            maxWidth: "40rem",
                        }}>
                        {t("heroTitle")}
                    </Typography>
                </motion.div>
                <motion.div
                    initial={{opacity: 0, y: 20}}
                    animate={{opacity: 1, y: 0}}
                    transition={{duration: 0.5, delay: 0.1}}>
                    <Typography
                        level="body-lg"
                        maxWidth={"18rem"}
                        textAlign={"center"}>
                        {t("heroDescription")}
                    </Typography>
                </motion.div>
                <motion.div
                    initial={{opacity: 0, y: 20}}
                    animate={{opacity: 1, y: 0}}
                    transition={{duration: 0.5, delay: 0.2}}>
                    <Stack direction={{xs: "column", sm: "row"}} gap={2} flexWrap="wrap" justifyContent="center">
                        <Button
                            size="lg"
                            onClick={() => handleStartPreparing()}>
                            {t("primaryCallToActionLabelAlt")}
                        </Button>
                        <Button
                            size="lg"
                            variant="outlined"
                            onClick={() => navigate("/pricing")}>
                            {t("viewPricingActionButtonLabel")}
                        </Button>
                    </Stack>
                </motion.div>
            </Stack>

            {/* Trust badges */}
            <Stack
                direction={{xs: "column", sm: "row"}}
                justifyContent="center"
                gap={5}
                alignItems="center"
                paddingY={3}
                sx={{bgcolor: "background.level1"}}>
                {TRUST_BADGES.map((badge, i) => (
                    <motion.div
                        key={badge.label}
                        initial={{opacity: 0, y: 10}}
                        whileInView={{opacity: 1, y: 0}}
                        viewport={{once: true}}
                        transition={{delay: i * 0.1}}>
                        <Card
                            variant="soft"
                            sx={{
                                alignItems: "center",
                            }}
                            data-no-glow
                            data-no-animation>
                            <badge.icon style={{color: "var(--joy-palette-primary-500)", fontSize: 40}}/>
                            <Box>
                                <Typography textAlign={"center"} level="title-lg">{t(badge.label)}</Typography>
                                <Typography textAlign={"center"} level="body-sm" textColor="neutral.500">
                                    {t(badge.sub)}
                                </Typography>
                            </Box>
                        </Card>
                    </motion.div>
                ))}
            </Stack>

            {/* Why Linguascore */}
            <Stack paddingX={{xs: 2, sm: 4, lg: 10}} paddingY={6} gap={4}>
                <Typography level="h1" textAlign="center" sx={{fontSize: {xs: "1.5rem", md: "1.75rem"}}}>
                    {t("whyTitle")}
                </Typography>
                <Grid container spacing={3}>
                    {WHY_ITEMS.map((item, i) => (
                        <Grid xs={12} sm={6} key={item.title}>
                            <motion.div
                                initial={{opacity: 0, y: 20}}
                                whileInView={{opacity: 1, y: 0}}
                                viewport={{once: true}}
                                transition={{delay: i * 0.1}}>
                                <Card
                                    variant="outlined"
                                    sx={{
                                        padding: 2,
                                        height: "100%",
                                    }}
                                    data-no-glow
                                    data-no-animation>
                                    <Stack gap={1.5}>
                                        <Box
                                            sx={{
                                                padding: 2,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                alignSelf: "center",
                                                borderRadius: 10,
                                                bgcolor: "primary.softBg",
                                                color: "primary.softColor",
                                            }}>
                                            <item.icon size={22} style={{color: "currentColor", fontSize: 40}}/>
                                        </Box>
                                        <Stack gap={1} alignItems={"center"}>
                                            <Typography level="title-lg">{t(item.title)}</Typography>
                                            <Typography level="body-sm" textAlign={"center"} textColor="neutral.600">
                                                {t(item.description)}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </Card>
                            </motion.div>
                        </Grid>
                    ))}
                </Grid>
            </Stack>

            {/* Exams we prepare for */}
            <Stack
                paddingX={{xs: 2, sm: 4, lg: 8}}
                paddingY={6}
                gap={4}
                sx={{bgcolor: "background.level1"}}>
                <Typography level="h2" textAlign="center" sx={{fontSize: {xs: "1.5rem", md: "1.75rem"}}}>
                    {t("examTitle")}
                </Typography>
                <Grid container spacing={2}>
                    {EXAMS.map((exam, i) => (
                        <Grid xs={12} sm={6} md={3} key={exam.name}>
                            <motion.div
                                initial={{opacity: 0, scale: 0.98}}
                                whileInView={{opacity: 1, scale: 1}}
                                viewport={{once: true}}
                                transition={{delay: i * 0.08}}>
                                <Card
                                    variant="soft"
                                    sx={{
                                        padding: 2,
                                        height: "100%",
                                        borderLeft: 4,
                                        borderColor: exam.color[0],
                                    }}
                                    data-no-glow
                                    data-no-animation>
                                    <Stack gap={0.5}>
                                        <Typography level="h4" sx={{color: exam.color[0]}}>
                                            {t(exam.name)}
                                        </Typography>
                                        <Typography level="body-sm" textColor="neutral.600">
                                            {t(exam.description)}
                                        </Typography>
                                    </Stack>
                                </Card>
                            </motion.div>
                        </Grid>
                    ))}
                </Grid>
            </Stack>

            {/* Pricing */}
            <Stack paddingX={{xs: 2, sm: 4, lg: 8}} paddingY={6} gap={4} alignItems="center">
                <Stack gap={1}>
                    <Typography level="h1" textAlign="center" sx={{fontSize: {xs: "1.5rem", md: "1.75rem"}}}>
                        {t("pricingTitle")}
                    </Typography>
                    <Typography level="body-lg" textAlign="center" maxWidth="40ch">
                        {t("pricingDescription")}
                    </Typography>
                </Stack>

                <Grid container spacing={2} sx={{maxWidth: 900, width: "100%", justifyContent: "center"}}>
                    {DEFAULT_SUBSCRIPTION_PLANS.map((plan, i) => (
                        <Grid xs={12} sm={6} md={4} key={plan.id ?? i}>
                            <motion.div
                                initial={{opacity: 0, y: 20}}
                                whileInView={{opacity: 1, y: 0}}
                                viewport={{once: true}}
                                transition={{duration: 0.4, delay: i * 0.08}}>

                                <PricingCardComponent subscription={plan}/>
                            </motion.div>
                        </Grid>
                    ))}
                </Grid>
                <Button
                    variant="outlined"
                    size={"lg"}
                    component={Link}
                    to="/pricing"
                    sx={{mt: 1}}>
                    {t("viewFullPricingActionButtonLabel")}
                </Button>
            </Stack>

            {/* Testimonials */}
            <Stack paddingX={{xs: 2, sm: 4, lg: 8}} paddingY={6} gap={3} alignItems="center">
                <Typography level="h1" textAlign="center" sx={{fontSize: {xs: "1.5rem", md: "1.75rem"}}}>
                    {t("testimonialTitle")}
                </Typography>
                <Card
                    variant="outlined"
                    sx={{
                        maxWidth: 600,
                        padding: 3,
                        textAlign: "center",
                        position: "relative",
                    }}
                    data-no-glow
                    data-no-animation>
                    <Quote
                        size={32}
                        style={{
                            color: "var(--joy-palette-neutral-400)",
                            position: "absolute",
                            top: 16,
                            left: 20,
                        }}
                    />
                    <Typography level="body-lg" sx={{fontStyle: "italic", py: 2}}>
                        {t(TESTIMONIALS[testimonialIndex].quote)}
                    </Typography>
                    <Typography level="body-sm" textColor="neutral.500">
                        — {t(TESTIMONIALS[testimonialIndex].author)}
                    </Typography>
                    <Stack direction="row" justifyContent="center" gap={1.5} marginTop={2}>
                        {TESTIMONIALS.map((_, i) => (
                            <Box
                                key={i}
                                color="neutral"
                                sx={{
                                    width: "12px",
                                    height: "12px",
                                    cursor: "pointer",
                                    backgroundColor: i === testimonialIndex ? "primary.400" : "neutral.300",
                                    borderRadius: 10
                                }}
                                onClick={() => setTestimonialIndex(i)}
                                aria-label={`Testimonial ${i + 1}`}
                            />
                        ))}
                    </Stack>
                </Card>
            </Stack>

            {/* FAQ */}
            <Stack paddingX={{xs: 2, sm: 4, lg: 8}} paddingY={6} gap={2} sx={{bgcolor: "background.level1"}}>
                <Typography level="h1" textAlign="center" sx={{fontSize: {xs: "1.5rem", md: "1.75rem"}}}>
                    {t("faqTitle")}
                </Typography>
                <Stack maxWidth={700} gap={2} margin="0 auto" width="100%">
                    {FAQ_ITEMS.map((faq, i) => (
                        <AccordionGroup size={"lg"} variant={"outlined"} sx={{borderRadius: 10, overflow: "hidden"}}>
                            <Accordion>
                                <AccordionSummary>{t(faq.q)}</AccordionSummary>
                                <AccordionDetails sx={{textAlign: "left"}}>{t(faq.a)}</AccordionDetails>
                            </Accordion>
                        </AccordionGroup>
                    ))}
                </Stack>
            </Stack>

            {/* Footer CTA */}
            <Stack
                paddingX={{xs: 2, sm: 4, lg: 8}}
                paddingY={8}
                alignItems="center"
                gap={2}>
                <Typography level="h3" sx={{fontSize: {xs: "1.35rem", md: "1.75rem"}}}>
                    {t("footerCallToActionTitle")}
                </Typography>
                <Typography level="body-md" textColor="neutral.600">
                    {t("footerCallToActionDescription")}
                </Typography>
                <Stack direction={{xs: "column", sm: "row"}} gap={2}>
                    <Button
                        size="lg"
                        component={Link}
                        to="/register">
                        {t("footerCallToActionPrimaryButtonLabel")}
                    </Button>
                    <Button size="lg" variant="outlined" onClick={handleLogin}>
                        {t("footerCallToActionSecondaryButtonLabel")}
                    </Button>
                </Stack>
            </Stack>
        </Stack>)
}

export default GuestHome
